import { useState } from 'react'

import Styles from './SelectContentTypes.module.scss'

import { ArrowRight } from 'almanac-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Form } from 'components/Wizards/Form/Form'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { Category, categoriesWithDesignChannels } from './categories'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { RadioGroup } from 'components/RadioGroup'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ChannelOption } from 'components/Wizards/CampaignWizard/Types'
import { useFormikContext } from 'formik'
import { Values } from 'components/Wizards/schemas/TurnInto'
import { turnIntoSchemaModifier } from './turnIntoSchemaModifier'
import { FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { useSections } from 'components/Wizards/Wizard/Form/Sections/SectionsProvider'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { WizardButton, WizardButtonContent } from 'components/Wizards/WizardsV3/WizardButton/WizardButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { WizardListItem } from 'components/Wizards/WizardsDrawer/WizardsDrawerV3/ListItem/ListItem'
import { AspectRatioKey, DesignChannels } from 'components/NewFileModal/Types'
import { TextChannelKey } from 'types'

interface Props {
  goBack: () => void
  isMultiSelect: Boolean
  goForward: () => void
  initialIsCampaign?: boolean
  isFromDoc?: boolean
  topic?: string
}

export const channelTypeToName = (categories: Array<Category>, channelType: ChannelType) => {
  let channelName = ''
  categories.forEach((category) => {
    // check nested categories
    if (category.categories) {
      channelName = channelTypeToName(category.categories, channelType)
    } else {
      category.channels.forEach((channel) => {
        if (channel.type === channelType) {
          channelName = channel.name
        }
      })
    }
  })

  return channelName
}

export const SelectContentTypes = ({
  goBack,
  goForward,
  isMultiSelect = true,
  initialIsCampaign,
  isFromDoc,
  topic,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('')
  const formikProps = useFormikContext<Values>()
  const { isMobile } = useResponsiveDesign()

  const { channels = [] } = formikProps.values
  const setChannels = (channels: ChannelOption[]) => formikProps.setFieldValue('channels', channels)
  const { createFieldsetCompletion } = useSections()
  const categories = categoriesWithDesignChannels

  const handleGoFoward = () => {
    createFieldsetCompletion()
    goForward()
  }

  const handleCheckboxChange = (isSelected: boolean, option: ChannelOption) => {
    if (isSelected) {
      setChannels([...channels, option])
    } else {
      setChannels(channels.filter((c) => c.channel_type !== option.channel_type))
    }
  }

  const handleRadioGroupChange = (type: string) => {
    setChannels([
      {
        channel_type: type as ChannelType,
        title: channelTypeToName(categories, type as ChannelType),
      },
    ])
  }
  const filterCategory = (category: Category, searchTerm: string): Category | null => {
    if (searchTerm.length === 0) return category

    // filter sub categories
    if (category.categories) {
      const filteredCategories = category.categories.map((subCategory) => filterCategory(subCategory, searchTerm))
      return {
        ...category,
        categories: filteredCategories.filter((category): category is Category => category !== null),
      }
    }

    const filteredChannels = category.channels.filter((channel) => {
      return !!channel.tags.find((tag) => tag.includes(searchTerm.toLowerCase()))
    })

    if (filteredChannels.length === 0) return null

    return { ...category, channels: filteredChannels }
  }

  const filteredCategories = categories.map((category) => filterCategory(category, searchTerm))

  const renderMultiSelectCategory = (category: Category | null, isSubCategory = false) => {
    if (category === null) return category

    return (
      <div key={category.title} className={Styles.categoryContainer}>
        <ResponsiveDesign.Mobile>
          {!category.categories && <span className={Styles.categoryTitle}>{category.title}</span>}
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <Heading level={isSubCategory ? 4 : 3}>{category.title}</Heading>
        </ResponsiveDesign.Desktop>
        <div className={category.categories ? Styles.subCategoriesContainer : Styles.categoryChannelsContainer}>
          {category.categories
            ? category.categories.map((subCategory) => renderMultiSelectCategory(subCategory, true))
            : category.channels
                .filter((channel) => (isMultiSelect ? channel.enabledForCampaign !== false : true))
                .map(({ type, name, icon: Icon, width, height, image }) => {
                  const channelKey = DesignChannels[type as AspectRatioKey] ?? type

                  return (
                    <div key={`${type}-${name}`} className={Styles.channelContainer}>
                      <Checkbox
                        size={isMobile ? 'md' : 'lg'}
                        isSelected={channels.some(
                          (c) => c.channel_type === channelKey && c.width === width && c.height === height,
                        )}
                        onChange={(isSelected) =>
                          handleCheckboxChange(isSelected, {
                            channel_type: channelKey,
                            title: name,
                            width,
                            height,
                          })
                        }
                        aria-label={type}
                      >
                        <ResponsiveDesign.Desktop>
                          <WizardButtonContent onPress={() => {}} title={name} image={image} size="sm" />
                        </ResponsiveDesign.Desktop>
                        <ResponsiveDesign.Mobile>
                          <div className={Styles.channelIconAndNameContainer}>
                            <div className={Styles.iconContainer}>
                              <Icon size={24} />
                            </div>
                            <span className={Styles.channelName}>{name}</span>
                          </div>
                        </ResponsiveDesign.Mobile>
                      </Checkbox>
                    </div>
                  )
                })}
        </div>
      </div>
    )
  }

  const renderSingleSelectCategory = (category: Category | null, isSubCategory = false) => {
    if (category === null) return category

    return (
      <div key={category.title} className={Styles.categoryContainer}>
        <ResponsiveDesign.Mobile>
          {!category.categories && <span className={Styles.categoryTitle}>{category.title}</span>}
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <Heading level={isSubCategory ? 4 : 3}>{category.title}</Heading>
        </ResponsiveDesign.Desktop>
        <div className={category.categories ? Styles.subCategoriesContainer : Styles.categoryChannelsContainer}>
          {category.categories
            ? category.categories.map((subCategory) => renderSingleSelectCategory(subCategory, true))
            : category.channels.map(({ type }) => {
                if (isMobile) {
                  return (
                    <WizardListItem
                      key={type}
                      channelKey={type as TextChannelKey}
                      schemaModifier={turnIntoSchemaModifier}
                      initialFormStatus={FormStatus.ManuallyFilled}
                      initialValues={{
                        transcription_id: formikProps.values.transcription_id,
                        topic,
                      }}
                    />
                  )
                }

                return (
                  <WizardButton
                    key={type}
                    channelKey={type as TextChannelKey}
                    schemaModifier={turnIntoSchemaModifier}
                    initialFormStatus={FormStatus.ManuallyFilled}
                    initialValues={{
                      transcription_id: formikProps.values.transcription_id,
                      topic,
                    }}
                    size="sm"
                  />
                )
              })}
        </div>
      </div>
    )
  }

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields>
            <div className={Styles.container}>
              <div className={Styles.searchInputContainer}>
                <Heading level={5}>
                  {initialIsCampaign
                    ? "Select the content you'd like to be part of your multi channel campaign."
                    : 'Select the content you want to generate from the source text.'}
                </Heading>
                <Input
                  size="lg"
                  name="Search Content Type"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search for content type..."
                />
              </div>
              {isMultiSelect ? (
                <div className={Styles.categoriesContainer}>
                  {filteredCategories.map((category) => {
                    return renderMultiSelectCategory(category)
                  })}
                </div>
              ) : (
                <RadioGroup onChange={handleRadioGroupChange} aria-label="Single Select Doc Type">
                  <div className={Styles.categoriesContainer}>
                    {filteredCategories.map((category) => {
                      return renderSingleSelectCategory(category)
                    })}
                  </div>
                </RadioGroup>
              )}
            </div>
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>
      <Form.Actions isSticky>
        {isFromDoc ? <div /> : <Form.PreviousButton onPress={goBack}>Previous Step</Form.PreviousButton>}
        {isMultiSelect && (
          <Button
            size="lg"
            onPress={handleGoFoward}
            endIcon={ArrowRight}
            isDisabled={isMultiSelect ? channels.length === 0 : channels.length !== 1}
          >
            Confirm Details
          </Button>
        )}
      </Form.Actions>
    </Form.Root>
  )
}
